import React, { createContext, useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify';
import * as Api from '../Services/Api'
import { searchCantiques, normalizeSearchText } from '../utils/search'
import {
  loadFavorites, saveFavorites, loadRecents, saveRecents, pushRecent, loadCatalogCache, saveCatalogPart,
} from '../utils/localLibrary'

export const CantiqueContextAPI = createContext();

const CantiqueContext = ({ children }) => {
  const [cantiques, setCantiques] = useState([]);
  const [catalogStatus, setCatalogStatus] = useState('loading'); // 'loading' | 'ready' | 'cached' | 'error'
  const [favoriteIds, setFavoriteIds] = useState(loadFavorites);
  const [recentIds, setRecentIds] = useState(loadRecents);
  // which collection the sidebar list shows: 'all' | 'favorites' | 'recents'
  const [listMode, setListMode] = useState('all');
  // store the full language objects we get from the API. each entry
  // contains an id and name. some components (admin) must know the id
  // so we can't just keep strings here.
  const [languages, setLanguages] = useState([]); // array of {id,name}
  const [selectedCantique, setSelectedCantique] = useState(null);
  // tracks whether we're currently loading details for a cantique
  const [loading, setLoading] = useState(false);
  // id of the cantique we're currently fetching (used to show a spinner next to the title)
  const [pendingCantiqueId, setPendingCantiqueId] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const inactivityTimer = useRef(null);

  const resetInactivity = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      // on prolonged inactivity go back to home (croix) and clear selection
      setSelectedCantique(null);
      navigate('/home');
    }, 20 * 60 * 1000); // 20 minutes
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  // UI state: whether the sidebar (mobile) is open
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // view style for the cantique list, either 'list' or 'number'
  // persist user's preference in localStorage so it survives reloads
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('viewMode');
      return saved === 'number' ? 'number' : 'list';
    } catch (_err) {
      return 'list';
    }
  });
  const [duplicateCantique, setDuplicateCantique] = useState(null);
  // the alphabetical sort was removed: drop its saved choice
  useEffect(() => {
    try {
      localStorage.removeItem('cantiqueSort');
    } catch (_err) {
      // ignore
    }
  }, []);

  // keep localStorage in sync with the state
  useEffect(() => {
    try {
      localStorage.setItem('viewMode', viewMode);
    } catch (_err) {
      // ignore
    }
  }, [viewMode]);

  const normalizeCantiqueData = (cantique) => {
    const normalizedCouplets = (cantique?.couplets || [])
      .map((couplet, index) => ({
        ...couplet,
        numero: couplet?.numero ?? couplet?.number ?? index + 1,
        text: couplet?.text ?? couplet?.texte ?? '',
      }))
      .filter((couplet) => couplet.text);

    const normalizedRefrain = typeof cantique?.refrain === 'string'
      ? { text: cantique.refrain }
      : cantique?.refrain || null;

    return {
      ...cantique,
      _id: cantique?._id || cantique?.id,
      id: cantique?._id || cantique?.id,
      number: cantique?.number ?? cantique?.numero ?? null,
      langue: cantique?.langue?.name || cantique?.langue || '',
      refrain: normalizedRefrain,
      couplets: normalizedCouplets,
    };
  };

  /**
   * Select a cantique by id. When an id is provided we enter a brief
   * "loading" state before the details are finally set in order to give
   * the user a small animation/feedback when they tap on a title.
   */
  const reloadCantiques = () => Api.fetchCantiques().then(res => {
    setCantiques(res.data.map(normalizeCantiqueData));
    setCatalogStatus('ready');
    saveCatalogPart('cantiques', res.data);
  });

  useEffect(() => {
    // fetch fresh list from API; fall back to the copy saved on this device when offline
    reloadCantiques()
      .catch(err => {
        console.error('load cantiques', err);
        const cached = loadCatalogCache().cantiques;
        if (Array.isArray(cached) && cached.length > 0) {
          setCantiques(cached.map(normalizeCantiqueData));
          setCatalogStatus('cached');
        } else {
          setCatalogStatus('error');
        }
      });

    // also load languages
    const applyLanguages = (list) => setLanguages(list
      .map(l => ({ id: l._id, name: l.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
    );
    Api.fetchLangues()
      .then(res => {
        applyLanguages(res.data);
        saveCatalogPart('langues', res.data);
      })
      .catch(err => {
        console.error('load langues', err);
        const cached = loadCatalogCache().langues;
        if (Array.isArray(cached)) applyLanguages(cached);
      });

    // start inactivity watcher
    const events = ['click', 'keydown', 'mousemove', 'touchstart'];
    events.forEach(evt => window.addEventListener(evt, resetInactivity));
    resetInactivity();
    return () => {
      events.forEach(evt => window.removeEventListener(evt, resetInactivity));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, []);

  const selectCantique = (id) => {
    resetInactivity();
    // if null explicitly requested, clear selection and navigate home
    if (id === null) {
      setSelectedCantique(null);
      setPendingCantiqueId(null);
      navigate('/');
      return;
    }

    // toggle off if clicking the currently selected item.
    if (selectedCantique?.id === id) {
      setSelectedCantique(null);
      setPendingCantiqueId(null);
      navigate('/');
      return;
    }

    const cantique = cantiques.find(c => c.id === id || c._id === id);
    if (cantique) {
      setLoading(true);
      setPendingCantiqueId(id);
      // simulate a small delay so the spinner is visible
      setTimeout(() => {
        setSelectedCantique(cantique);
        setLoading(false);
        setPendingCantiqueId(null);
        navigate(`/cantiques/${id}`);
      }, 400);
    }
    // on mobile, close the sidebar immediately when selecting a cantique
    if (typeof window !== 'undefined' && window.innerWidth <= 1000) {
      setSidebarOpen(false);
    }
    resetInactivity();
  };

  // Modifier un cantique existant
  const modifyCantique = async (id, updatedData) => {
    try {
      const res = await Api.updateCantique(id, updatedData);
      const updated = normalizeCantiqueData(res.data);
      setCantiques(cantiques.map(c =>
        c._id === id || c.id === id ? updated : c
      ));
      if (selectedCantique && (selectedCantique.id === id || selectedCantique._id === id)) {
        setSelectedCantique(updated);
      }
      return true;
    } catch (err) {
      console.error('update cantique', err);
      toast.error(`Erreur lors de la modification du cantique : ${err.response?.data?.message || err.message}`);
      return false;
    }
  };

  // Supprimer un cantique
  const deleteCantique = async (id) => {
    try {
      await Api.deleteCantique(id);
      // the server closes the numbering gap: mirror it locally
      setCantiques(current => {
        const removed = current.find(c => c._id === id || c.id === id);
        const rest = current.filter(c => c._id !== id && c.id !== id);
        const gone = removed?.number;
        if (typeof gone !== 'number' || rest.some(c => c.number === gone)) return rest;
        return rest.map(c => (typeof c.number === 'number' && c.number > gone ? { ...c, number: c.number - 1 } : c));
      });
      if (selectedCantique && (selectedCantique._id === id || selectedCantique.id === id)) {
        setSelectedCantique(null);
      }
    } catch (err) {
      console.error('delete cantique', err);
      toast.error(`Erreur lors de la suppression du cantique : ${err.response?.data?.message || err.message}`);
    }
  };

  // Ajouter un nouveau cantique
  const addCantique = async (newCantique) => {
    // Check for duplicate title
    const newTitle = normalizeSearchText(newCantique.title);
    const existing = cantiques.find(c => normalizeSearchText(c.title) === newTitle);
    if (existing) {
      setDuplicateCantique(existing);
      return false; // Indicate not added
    }
    try {
      const res = await Api.createCantique(newCantique);
      setCantiques([...cantiques, normalizeCantiqueData(res.data)]);
      return true; // Indicate added
    } catch (err) {
      console.error('create cantique', err);
      toast.error(`Erreur lors de la création du cantique : ${err.response?.data?.message || err.message}`);
      return false;
    }
  };

  // Filtrer les cantiques basé sur la recherche (numéro, titre, paroles) et la langue sélectionnée
  const favoriteCantiques = useMemo(() => {
    const favorites = new Set(favoriteIds);
    return cantiques.filter(c => favorites.has(c.id));
  }, [cantiques, favoriteIds]);
  const recentCantiques = useMemo(
    () => recentIds.map(id => cantiques.find(c => c.id === id)).filter(Boolean),
    [cantiques, recentIds]
  );
  const collectionCantiques = listMode === 'favorites'
    ? favoriteCantiques
    : listMode === 'recents' ? recentCantiques : cantiques;
  // By number (1 → N, oldest first); « Récents » keeps the consultation order.
  const languageCantiques = useMemo(() => {
    const list = selectedLanguage === null
      ? collectionCantiques
      : collectionCantiques.filter(c => c.langue === selectedLanguage);
    if (listMode === 'recents') return list;
    return [...list].sort((a, b) => (a.number ?? Infinity) - (b.number ?? Infinity));
  }, [collectionCantiques, selectedLanguage, listMode]);

  const isFavorite = (id) => favoriteIds.includes(id);
  const toggleFavorite = (id) => {
    setFavoriteIds(current => {
      const next = current.includes(id) ? current.filter(other => other !== id) : [...current, id];
      saveFavorites(next);
      return next;
    });
  };
  const clearRecents = () => {
    setRecentIds([]);
    saveRecents([]);
  };

  useEffect(() => {
    if (!selectedCantique?.id) return;
    setRecentIds(current => {
      const next = pushRecent(current, selectedCantique.id);
      saveRecents(next);
      return next;
    });
  }, [selectedCantique?.id]);
  const filteredCantiques = useMemo(
    () => searchCantiques(languageCantiques, searchTerm),
    [languageCantiques, searchTerm]
  );

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login?logout=true');
  };

  const value = {
    // Original data
    selectedCantique,
    selectCantique,
    allCantiques: cantiques,
    catalogStatus,
    filteredCantiques,

    // Favoris et récents (gardés sur l'appareil)
    favoriteIds,
    favoriteCount: favoriteCantiques.length,
    recentCount: recentCantiques.length,
    isFavorite,
    toggleFavorite,
    clearRecents,
    listMode,
    setListMode,
    loading,
    pendingCantiqueId,
    
    // Modification functions
    modifyCantique,
    deleteCantique,
    addCantique,
    
    // Search and filter state
    searchTerm,
    setSearchTerm,
    selectedLanguage,
    setSelectedLanguage,
    // Sidebar control for mobile
    sidebarOpen,
    setSidebarOpen,
    // list display mode ('list' or 'number')
    viewMode,
    setViewMode,
    reloadCantiques,
    
    // Duplicate modal
    duplicateCantique,
    setDuplicateCantique,
    
    // Authentication helpers
    logout,

    // Get unique languages (from API list)
    availableLanguages: languages,          // array of {id,name}
    // function to refresh languages list (can be called by other components)
    refreshLanguages: async () => {
      try {
        const res = await Api.fetchLangues();
        setLanguages(res.data
          .map(l => ({ id: l._id, name: l.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (e) {
        console.error('refresh languages', e);
      }
    }
  };

  // keep the URL and selection in sync - also handle direct link / refresh
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/cantiques/')) {
      const id = path.split('/')[2];
      const cantique = cantiques.find(c => c.id === id || c._id === id);
      if (cantique) {
        setSelectedCantique(cantique);
      }
    } else {
      setSelectedCantique(null);
    }
    resetInactivity();
  }, [location.pathname, cantiques]);

  return (
    <CantiqueContextAPI.Provider value={value}>
      {children}
    </CantiqueContextAPI.Provider>
  );
}

export default CantiqueContext